import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import ToasterHelper from '../../shared/helpers/toaster.helper';
import Request from '../../shared/request/Request';
import { API } from '../../shared/constants/API';
import Header from './header';
import { HttpStatusCode } from '../../shared/request/HttpStatusCode';
import { Dialog } from 'primereact/dialog';

function MainPage() {
  const { t } = useTranslation();
  const [validated, setValidated] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);

  const isValidEmail = (email: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
  };

  const validateEmail = (event: any) => {
    if (!isValidEmail(event.target.value)) {
      setEmailInvalid(true);
    } else {
      setEmailInvalid(false);
    }
    event.preventDefault();
  };

  function onSignUp(event: any) {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    if (form.checkValidity() === true) {
      const input = {
        email: event.target[0].value,
      };

      Request.post(`${API.USERS_SERVICE}/subscribeNewsletter`, { body: input })
        .then((result: any) => {
          console.log(result);
          if (result.status.toString() === HttpStatusCode.CONFLICT) {
            ToasterHelper.notify(t('MAIN_PAGE.VALIDATIONS.USER_DUPLICATE_EMAIL'), 'warn');
          } else if (result.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (result.status.toString() === HttpStatusCode.OK) {
            setIsEventModalVisible(true);
          } else {
            ToasterHelper.notify(t('MAIN_PAGE.FAILED_MESSAGE'), 'error');
          }
        })
        .catch((error: any) => {
          console.log(error);
          ToasterHelper.notify(t('MAIN_PAGE.FAILED_MESSAGE'), 'error');
          console.log(error);
        });
    } else {
      ToasterHelper.notify(t('MAIN_PAGE.VALIDATIONS.EMAIL_REQUIRED'), 'info');
    }
    setValidated(true);
  }

  // TODO - Add translations
  return (
    <>
      <div>
        <Header showLinks={true} />
        <ToastContainer />
        <Dialog
          className='col-md-6 flex'
          header={t('MAIN_PAGE.SUBSCRIBE_NEWSLETTER')}
          visible={isEventModalVisible}
          onHide={() => {
            if (!isEventModalVisible) return;
            setIsEventModalVisible(false);
          }}
          style={{ width: '75vw' }}
          breakpoints={{ '750px': '75vw', '641px': '100vw' }}>
          <div className='col-md-12 flex text-sm'>{t('MAIN_PAGE.USER_SUBSCRIBED')}</div>
          <div className='col-md-12 text-right'>
            <Button
              className='background button-border-radius cursor-pointer max-h-1/3'
              type='button'
              onClick={() => setIsEventModalVisible(false)}>
              <b>{t('COMMON.CLOSE')}</b>
            </Button>
          </div>
        </Dialog>

        <section className='hero'>
          <div className='container'>
            <div className='hero-content'>
              <h1>Unlock Exclusive Sports Deals</h1>
              <p>
                Join thousands of athletes getting gear at unbeatable prices. Never miss a deal on the equipment that powers your
                performance.
              </p>
              <a href='#newsletter' className='cta-button'>
                Get Weekly Deals
              </a>
            </div>
          </div>
        </section>

        <section className='features' id='features'>
          <div className='container'>
            <h2 className='section-title fade-in'>Why Athletes Choose Sports Deals</h2>
            <div className='features-grid'>
              <div className='feature-card fade-in'>
                <div className='feature-icon'>🏃</div>
                <h3>Curated for Athletes</h3>
                <p>
                  Every deal is handpicked by athletes, for athletes. We focus on quality gear that actually improves your performance, not
                  just trendy items.
                </p>
              </div>
              <div className='feature-card fade-in'>
                <div className='feature-icon'>⚡</div>
                <h3>Lightning Fast Alerts</h3>
                <p>
                  Be the first to know about flash sales and limited-time offers. Our real-time notifications ensure you never miss out on
                  the best deals.
                </p>
              </div>
              <div className='feature-card fade-in'>
                <div className='feature-icon'>💰</div>
                <h3>Massive Savings</h3>
                <p>
                  Save up to 70% on premium brands like Nike, Adidas, Asics, and more. Our members save an average of $300 per year on
                  running gear.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className='newsletter' id='newsletter'>
          <div className='container'>
            <div className='newsletter-content fade-in'>
              <h2>Join the Sports Deals Community</h2>
              <p>
                Get exclusive access to member-only deals, early-bird discounts, and expert gear recommendations delivered straight to your
                inbox every week.
              </p>
              <Form noValidate validated={validated} onSubmit={onSignUp}>
                <input type='email' className='email-input' placeholder='Enter your email address' onChange={validateEmail} required />
                <Button type='submit' className='submit-btn' data-testid='subscribe-button'>
                  Subscribe Now
                </Button>
                <div>{emailInvalid && <small className='text-danger'>{t('MAIN_PAGE.VALIDATIONS.USER_EMAIL_VALID')}</small>}</div>
              </Form>
            </div>
          </div>
        </section>

        <section id='coming-soon'>
          <div className='container'>
            <h2 className='section-title fade-in'>Coming next</h2>
            <p className='fade-in fade-in-extra'>
              We&apos;re constantly evolving to serve our athletic community better. Here&apos;s what we&apos;re working on to enhance your
              sports journey:
            </p>
            <div className='coming-soon-grid'>
              <div className='coming-soon-item fade-in'>
                <div className='coming-icon'>📅</div>
                <h4>Season Planner</h4>
                <p>Plan your entire racing season from preparation races up to your main races.</p>
              </div>
              <div className='coming-soon-item fade-in'>
                <div className='coming-icon'>🏋️</div>
                <h4>Training Plans</h4>
                <p>Custom training programs for every distance and fitness level.</p>
              </div>
              <div className='coming-soon-item fade-in'>
                <div className='coming-icon'>📊</div>
                <h4>Performance Analytics</h4>
                <p>Advanced metrics and insights to track your progress and optimize your training effectiveness.</p>
              </div>
              <div className='coming-soon-item fade-in'>
                <div className='coming-icon'>🤝</div>
                <h4>Community Hub</h4>
                <p>Connect with fellow athletes, share experiences, and get motivated by our supportive community.</p>
              </div>
            </div>
          </div>
        </section>

        <footer>
          <div className='container'>
            <p>&copy; 2025 Sports Deals. Empowering athletes with gear at unbeatable prices.</p>
          </div>
        </footer>
      </div>
      <div>
        <Outlet />
      </div>
    </>
  );
}

export default MainPage;
